import styled from '@emotion/styled';
import { Button, ActionIcon, Textarea, Loader, Popover, Menu } from '@mantine/core';
import { getHotkeyHandler, useHotkeys, useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../core/context';
import { useAppDispatch, useAppSelector } from '../store';
import { selectMessage, setMessage } from '../store/message';
import { selectSettingsTab } from '../store/settings-ui';
import { clearFlaggedMessage } from '../store/ui';
import { speechRecognition, supportsSpeechRecognition } from '../core/speech-recognition-types'
import { useWhisper } from '@chengsokdara/use-whisper';
// Quick settings removed
import { useOption } from '../core/options/use-option';
import { HARDCODED_VALUES } from '../hardcoded-values';
import { isProxySupported } from '../core/chat/openai';
import { parameterOptions } from '../global-options/parameters';
import { inputTools, getActiveTool } from '../input-tools';

const CHARACTER_LIMIT = 1500;

const Container = styled.div`
    background: #292933;
    border-top: thin solid #393933;
    padding: 0.6rem 1rem 1.2vh 1rem;

    .inner {
        max-width: 50rem;
        margin: auto;
        text-align: right;
    }

    .settings-button {
        margin: 0.5rem -0.4rem 0.5rem 1rem;
        font-size: 0.7rem;
        color: #999;
    }
`;

const ButtonContainer = styled.div`
    position: absolute;
    right: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    pointer-events: auto;
    
    @media (max-width: 40em) {
        right: 0.5rem;
        gap: 0.3rem;
    }
`;


const CircleButtonWrapper = styled.div`
    button {
        border-radius: 0.5rem !important;
        background-color: rgba(255, 255, 255, 0.1) !important;
        border: none !important;
        width: 1.5rem !important;
        height: 1.5rem !important;
        min-width: 1.5rem !important;
        min-height: 1.5rem !important;
        padding: 0 !important;
        color: #C1C2C5 !important;
        transition: all 0.15s ease !important;
        font-size: 0.75rem !important;
    }
    
    button:hover {
        background-color: #3d5a80 !important;
        color: #ffffff !important;
    }
`;

const SendButtonWrapper = styled.div`
    button {
        border-radius: 0.5rem !important;
        background-color: transparent !important;
        border: none !important;
        width: 1.75rem !important;
        height: 1.75rem !important;
        min-width: 1.75rem !important;
        min-height: 1.75rem !important;
        padding: 0 !important;
        color: #C1C2C5 !important;
    }
    
    button:hover {
        background-color: transparent !important;
    }
    
    button:active {
        transform: scale(0.9);
    }
`;

const InputFlexContainer = styled.div`
    display: flex;
    align-items: stretch;
`;

const ToolSegment = styled.div`
    border-radius: 2rem 0 0 2rem;
    background-color: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.4rem;
    min-width: 1.5rem;
    cursor: pointer;
    transition: background-color 0.15s ease;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.15);
    }
    
    i {
        color: #C1C2C5;
        font-size: 0.9rem;
        transition: color 0.15s ease;
    }
    
    &:hover i {
        color: #ffffff;
    }
`;

const ToolSegmentWithLabel = styled.div`
    border-radius: 2rem 0 0 2rem;
    background-color: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.6rem;
    gap: 0.4rem;
    cursor: pointer;
    transition: background-color 0.15s ease;
    white-space: nowrap;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.15);
    }
    
    i, span {
        color: #C1C2C5;
        font-size: 0.85rem;
        transition: color 0.15s ease;
    }
    
    &:hover i, &:hover span {
        color: #ffffff;
    }
`;

export declare type OnSubmit = (name?: string) => Promise<boolean>;

export interface MessageInputProps {
    disabled?: boolean;
}

export default function MessageInput(props: MessageInputProps) {
    const message = useAppSelector(selectMessage);
    const [recording, setRecording] = useState(false);
    const [speechError, setSpeechError] = useState<string | null>(null);
    const hasVerticalSpace = useMediaQuery('(min-height: 1000px)');
    // Using hardcoded values instead of options
    const useOpenAIWhisper = HARDCODED_VALUES.speechRecognition.useWhisper;

    const [initialMessage, setInitialMessage] = useState('');
    const {
        transcribing,
        transcript,
        startRecording,
        stopRecording,
    } = useWhisper({
        apiKey: ' ', // Dummy key since backend proxy handles authentication
        streaming: false,
    });

    const navigate = useNavigate();
    const context = useAppContext();
    const dispatch = useAppDispatch();
    const intl = useIntl();

    // Model selection
    const modelOption = useOption<string>('parameters', 'model', context.id || undefined);
    const model = modelOption[0];
    const setModel = modelOption[1];
    const onModelChange = useCallback((value: string | null) => {
        if (value) {
            setModel(value as string);
        }
    }, [setModel]);

    // Tool state management - get all tool parameter values
    const toolParameterValues: Record<string, any> = {};
    const toolSetters: Record<string, (value: any) => void> = {};
    
    inputTools.forEach(tool => {
        const [value, setValue] = useOption<boolean>('tools', tool.parameterKey, context.id || undefined);
        toolParameterValues[tool.parameterKey] = value;
        toolSetters[tool.parameterKey] = setValue;
    });

    // Get currently active tool
    const activeTool = getActiveTool(toolParameterValues);
    
    // State for hover effect on active tool button
    const [toolButtonHovered, setToolButtonHovered] = useState(false);

    // Toggle tool on/off
    const toggleTool = useCallback((toolId: string) => {
        const tool = inputTools.find(t => t.id === toolId);
        if (tool && toolSetters[tool.parameterKey]) {
            const currentValue = toolParameterValues[tool.parameterKey];
            toolSetters[tool.parameterKey](!currentValue);
        }
    }, [toolParameterValues, toolSetters]);

    // Get model options from parameters
    const modelParameter = parameterOptions.options.find(opt => opt.id === 'model');
    const renderProps = typeof modelParameter?.renderProps === 'function' 
        ? modelParameter.renderProps(model, context.chat.options, context)
        : modelParameter?.renderProps;
    const modelOptions = renderProps?.options || [];
    
    // Get current model label
    const getCurrentModelLabel = () => {
        const currentOption = modelOptions.find(opt => opt.value === model);
        return currentOption?.label || 'Model';
    };

    // Measure button container for dynamic padding
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const [buttonContainerWidth, setButtonContainerWidth] = useState<number>(0);

    useEffect(() => {
        // Use requestAnimationFrame to ensure DOM has updated before measuring
        requestAnimationFrame(() => {
            if (buttonContainerRef.current) {
                const width = buttonContainerRef.current.offsetWidth;
                setButtonContainerWidth(width);
            }
        });
    }, [model, context.generating, recording, transcribing]);

    // Reset hover state when activeTool changes
    useEffect(() => {
        setToolButtonHovered(false);
    }, [activeTool]);

    // Separate input disabled state from submit disabled state
    const inputDisabled = props.disabled || context.generating;
    const submitDisabled = context.generating || !!context.flaggedMessage;

    const tab = useAppSelector(selectSettingsTab);

    // Using hardcoded values instead of options
    const showMicrophoneButton = HARDCODED_VALUES.speechRecognition.showMicrophone;
    const submitOnEnter = HARDCODED_VALUES.input.submitOnEnter;

    const sanitizeInput = useCallback((text: string): string => {
        // Remove null bytes and control characters (except newlines, tabs, and carriage returns)
        let sanitized = text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
        
        // Remove potentially dangerous patterns
        sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
        sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');
        sanitized = sanitized.replace(/javascript:/gi, '');
        sanitized = sanitized.replace(/data:text\/html/gi, '');
        
        // Limit consecutive special characters (but allow normal spacing)
        sanitized = sanitized.replace(/[!@#$%^&*()_+=\[\]{}|;':",./<>?`~-]{10,}/g, (match) => match.substring(0, 9));
        
        // Only limit excessive whitespace (more than 10 consecutive spaces/tabs)
        sanitized = sanitized.replace(/[ \t]{20,}/g, '                    '); // Limit to 20 spaces max
        
        return sanitized;
    }, []);

    const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const rawValue = e.target.value;
        const sanitizedValue = sanitizeInput(rawValue);
        
        // Clear flagged message state when user starts typing
        dispatch(clearFlaggedMessage());
        
        if (sanitizedValue.length <= CHARACTER_LIMIT) {
            dispatch(setMessage(sanitizedValue));
        }
    }, [dispatch, sanitizeInput]);

    const pathname = useLocation().pathname;

    const onSubmit = useCallback(async () => {
        // Don't submit if disabled (generating or flagged message)
        if (submitDisabled) {
            return;
        }
        
        setSpeechError(null);

        const id = await context.onNewMessage(message);

        if (id) {
            if (!window.location.pathname.includes(id)) {
                navigate('/chat/' + id);
            }
            dispatch(setMessage(''));
        }
    }, [context, message, dispatch, navigate, submitDisabled]);

    const onSpeechError = useCallback((e: any) => {
        console.error('speech recognition error', e);
        setSpeechError(e.message);

        try {
            speechRecognition?.stop();
        } catch (e) {
        }

        try {
            stopRecording();
        } catch (e) { }

        setRecording(false);
    }, [stopRecording]);

    const onHideSpeechError = useCallback(() => setSpeechError(null), []);

    const onSpeechStart = useCallback(async () => {
        let granted = false;
        let denied = false;

        try {
            const result = await navigator.permissions.query({ name: 'microphone' as any });
            if (result.state == 'granted') {
                granted = true;
            } else if (result.state == 'denied') {
                denied = true;
            }
        } catch (e) { }

        if (!granted && !denied) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
                stream.getTracks().forEach(track => track.stop());
                granted = true;
            } catch (e) {
                denied = true;
            }
        }

        if (denied) {
            onSpeechError(new Error('speech permission was not granted'));
            return;
        }

        try {
            if (!recording) {
                setRecording(true);

                if (useOpenAIWhisper || !supportsSpeechRecognition) {
                    setInitialMessage(message);
                    await startRecording();
                } else if (speechRecognition) {
                    const initialMessage = message;

                    speechRecognition.continuous = true;
                    speechRecognition.interimResults = true;

                    speechRecognition.onresult = (event) => {
                        let transcript = '';
                        for (let i = 0; i < event.results.length; i++) {
                            if (event.results[i].isFinal && event.results[i][0].confidence) {
                                transcript += event.results[i][0].transcript;
                            }
                        }
                        dispatch(setMessage(initialMessage + ' ' + transcript));
                    };

                    speechRecognition.start();
                } else {
                    onSpeechError(new Error('not supported'));
                }
            } else {
                if (useOpenAIWhisper || !supportsSpeechRecognition) {
                    await stopRecording();
                    setTimeout(() => setRecording(false), 500);
                } else if (speechRecognition) {
                    speechRecognition.stop();
                    setRecording(false);
                } else {
                    onSpeechError(new Error('not supported'));
                }
            }
        } catch (e) {
            onSpeechError(e);
        }
    }, [recording, message, dispatch, onSpeechError, setInitialMessage, useOpenAIWhisper, supportsSpeechRecognition]);

    useEffect(() => {
        if (useOpenAIWhisper || !supportsSpeechRecognition) {
            if (!transcribing && !recording && transcript?.text) {
                dispatch(setMessage(initialMessage + ' ' + transcript.text));
            }
        }
    }, [initialMessage, transcript, recording, transcribing, useOpenAIWhisper, supportsSpeechRecognition, dispatch]);

    useHotkeys([
        ['n', () => document.querySelector<HTMLTextAreaElement>('#message-input')?.focus()]
    ]);

    const blur = useCallback(() => {
        document.querySelector<HTMLTextAreaElement>('#message-input')?.blur();
    }, []);

    const rightSection = useMemo(() => {
        return (
            <>
                {context.generating && (<>
                        <Button variant="subtle" size="xs" compact onClick={() => {
                            context.chat.cancelReply(context.currentChat.chat?.id, context.currentChat.leaf!.id);
                        }}>
                            <FormattedMessage defaultMessage={"Cancel"} description="Label for the button that can be clicked while the AI is generating a response to cancel generation" />
                        </Button>
                        <Loader size="xs" style={{ padding: '0 0.8rem 0 0.5rem' }} />
                </>)}
                {!context.generating && (
                    <>
                        <Menu width={140} position="top">
                            <Menu.Target>
                                <Button size="xs" variant="subtle" compact
                                    styles={{
                                        root: {
                                            borderRadius: '0.5rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            border: 'none',
                                            padding: '0.25rem 0.75rem',
                                            minWidth: '100px',
                                            height: '1.75rem',
                                        }
                                    }}>
                                    {getCurrentModelLabel()}
                                    <i className="fa fa-chevron-down" style={{ fontSize: '80%', marginLeft: '0.75rem' }} />
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    padding: '0.5rem 0.75rem',
                                    borderBottom: '1px solid #444',
                                    marginBottom: '0.25rem'
                                }}>
                                    <span style={{ 
                                        color: '#cccccc', 
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        marginRight: 'auto'
                                    }}>
                                        Model
                                    </span>
                                </div>
                                {modelOptions.map((option) => (
                                    <Menu.Item 
                                        key={option.value}
                                        onClick={() => onModelChange(option.value)}
                                    >
                                        {model === option.value ? <i className="fa fa-check" style={{ marginRight: '0.5rem' }} /> : null}
                                        {option.label}
                                    </Menu.Item>
                                ))}
                        </Menu.Dropdown>
                    </Menu>
                    {showMicrophoneButton && <Popover width={200} position="bottom" withArrow shadow="md" opened={speechError !== null}>
                            <Popover.Target>
                                <CircleButtonWrapper>
                                    <ActionIcon size="xl" variant="transparent" onClick={onSpeechStart}>
                                        {transcribing && <Loader size="xs" />}
                                        {!transcribing && <i className="fa fa-microphone" style={{ fontSize: '90%', color: recording ? 'red' : 'inherit' }} />}
                                    </ActionIcon>
                                </CircleButtonWrapper>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                }}>
                                    <p style={{
                                        fontFamily: `"Work Sans", sans-serif`,
                                        fontSize: '0.9rem',
                                        textAlign: 'center',
                                        marginBottom: '0.5rem',
                                    }}>
                                        Sorry, an error occured trying to record audio.
                                    </p>
                                    <Button variant="light" size="xs" fullWidth onClick={onHideSpeechError}>
                                        Close
                                    </Button>
                                </div>
                            </Popover.Dropdown>
                    </Popover>}
                    <SendButtonWrapper>
                        <ActionIcon size="xl" variant="transparent" onClick={onSubmit}>
                            <i className="fa fa-paper-plane" style={{ fontSize: '90%' }} />
                        </ActionIcon>
                    </SendButtonWrapper>
                    </>
                )}
            </>
        );
    }, [recording, transcribing, onSubmit, onSpeechStart, props.disabled, context.generating, context.flaggedMessage, speechError, onHideSpeechError, showMicrophoneButton, model, onModelChange, activeTool, toggleTool, toolButtonHovered, inputTools]);

    const isLandingPage = pathname === '/';
    const shouldRender = !context.isShare && (isLandingPage || context.id);
    
    if (!shouldRender) {
        return null;
    }

    const hotkeyHandler = useMemo(() => {
        const keys = [
            ['Escape', blur, { preventDefault: true }],
            ['ctrl+Enter', onSubmit, { preventDefault: true }],

        ];
        if (submitOnEnter) {
            keys.unshift(['Enter', onSubmit, { preventDefault: true }]);
        }
        const handler = getHotkeyHandler(keys as any);
        return handler;
    }, [onSubmit, blur, submitOnEnter]);

    return <Container>
        <div className="inner">
            <InputFlexContainer>
                {!context.generating && (
                    !activeTool ? (
                        <Menu width={180} position="top-start">
                            <Menu.Target>
                                <ToolSegment>
                                    <i className="fa fa-plus" />
                                </ToolSegment>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    padding: '0.5rem 0.75rem',
                                    borderBottom: '1px solid #444',
                                    marginBottom: '0.25rem'
                                }}>
                                    <span style={{ 
                                        color: '#cccccc', 
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        marginRight: 'auto'
                                    }}>
                                        Tools
                                    </span>
                                </div>
                                {inputTools.map((tool) => (
                                    <Menu.Item 
                                        key={tool.id}
                                        onClick={() => toggleTool(tool.id)}
                                        icon={<i className={`fa fa-${tool.icon}`} />}
                                    >
                                        {tool.label}
                                    </Menu.Item>
                                ))}
                            </Menu.Dropdown>
                        </Menu>
                    ) : (
                        <ToolSegmentWithLabel 
                            onClick={() => toggleTool(activeTool.id)}
                            onMouseEnter={() => setToolButtonHovered(true)}
                            onMouseLeave={() => setToolButtonHovered(false)}
                        >
                            <i className={`fa fa-${toolButtonHovered ? 'times' : activeTool.icon}`} />
                            <span>{activeTool.activeLabel}</span>
                        </ToolSegmentWithLabel>
                    )
                )}
                <div style={{ position: 'relative', flex: 1 }}>
                    <Textarea disabled={inputDisabled}
                        id="message-input"
                        autosize
                        minRows={1}
                        maxRows={7}
                        maxLength={CHARACTER_LIMIT}
                        placeholder={intl.formatMessage({ defaultMessage: "Ask anything" })}
                        value={message}
                        onChange={onChange}
                        onKeyDown={hotkeyHandler}
                        styles={{
                            root: {
                                width: '100%',
                            },
                            input: {
                                paddingRight: buttonContainerWidth > 0 ? `calc(${buttonContainerWidth}px + clamp(0.5rem, 1.5vw, 1.25rem))` : '170px',
                                paddingLeft: '1rem',
                                borderRadius: context.generating ? '2rem' : '0 2rem 2rem 0',
                                border: 'none',
                                backgroundColor: '#3a3a44',
                                paddingTop: '1.25rem',
                                paddingBottom: '1.25rem',
                                fontSize: '1rem',
                                color: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                lineHeight: '1.5',
                                resize: 'none',
                                overflowY: 'auto',
                            }
                        }} />
                    <ButtonContainer ref={buttonContainerRef}>
                        {rightSection}
                    </ButtonContainer>
                </div>
            </InputFlexContainer>
            <div style={{
                textAlign: 'right',
                fontSize: '0.75rem',
                color: message.length > CHARACTER_LIMIT * 0.9 ? '#ff6b6b' : '#888',
                marginTop: '0.25rem',
                marginRight: '0.5rem'
            }}>
                {message.length}/{CHARACTER_LIMIT}
            </div>
        </div>
    </Container>;
}
